import React, { useState, useEffect, useCallback } from 'react'; // Add useEffect here
import { Link } from 'react-router-dom';
import axios from 'axios';
import SearchBar from './SearchBar';
import { getProducts } from '../../api/services/product/getproducts';
import { searchProduct } from '../../api/services/product/searchproduct';
import Loader from './../Common Utility/Loader';
import './ProductList.css';

// Utility debounce function
const debounce = (func, delay) => {
    let timeout;
    return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), delay);
    };
};

const ProductList = () => {
    const [products, setProducts] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [noResults, setNoResults] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const source = axios.CancelToken.source();

        const fetchProducts = async () => {
            setLoading(true);
            try {
                const response = await getProducts({ cancelToken: source.token });
                setProducts(response || []);
                setNoResults(response.length === 0);
                setError(null);
            } catch (error) {
                if (axios.isCancel(error)) {
                    console.warn('Request canceled:', error.message);
                } else {
                    setError('Failed to load products. Please try again later.');
                    setProducts([]);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchProducts();

        return () => {
            source.cancel('Component unmounted or new request initiated');
        };
    }, []);

    // eslint-disable-next-line
    const handleSearch = useCallback(
        debounce(async (query) => {
            setSearchQuery(query);
            setLoading(true);
            const source = axios.CancelToken.source();

            try {
                if (query) {
                    const response = await searchProduct(query, { cancelToken: source.token });
                    setProducts(response || []);
                    setSuggestions(response || []); // Update suggestions for the dropdown
                    setNoResults(response.length === 0);
                } else {
                    const response = await getProducts({ cancelToken: source.token });
                    setProducts(response || []);
                    setSuggestions([]); // Clear suggestions when query is empty
                    setNoResults(false);
                }
                setError(null);
            } catch (err) {
                if (axios.isCancel(err)) {
                    console.warn('Search request canceled:', err.message);
                } else {
                    setError('Failed to search products. Please try again later.');
                    setProducts([]);
                    setSuggestions([]);
                }
            } finally {
                setLoading(false);
            }

            return () => {
                source.cancel('Search request canceled due to new input');
            };
        }, 300),
        [] // No dependencies since debounce is now outside the component
    );

    if (loading) {
        return <Loader />;
    }

    return (
            <div className="product-list-container">
                <h1>Welcome to Our Retail Store</h1>
                <p>Discover amazing products at unbeatable prices!</p>

                <SearchBar onSearch={handleSearch} suggestions={suggestions} />
                {error ? (
                    <div className="error-message">
                        <p>{error}</p>
                    </div>
                ) : noResults ? (
                    <div className="no-results-message">
                        <p>No products found for "{searchQuery}". Please try a different search.</p>
                    </div>
                ) : (
                    <div className="product-grid">
                        {products.map((product) => (
                            <div className="product-card" key={product.product_id}>
                                <Link to={`/products/${product.product_id}`}>
                                    <img
                                        src={product.images}
                                        alt={product.title}
                                        className="product-image"
                                        loading="lazy"
                                    />
                                    <h2 className="product-name">{product.title}</h2>
                                </Link>
                            </div>
                        ))}
                    </div>
                )}
            </div>
    );
};

export default ProductList;